import { NativeImage } from "@/components/native/NativeImage";
import React, { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { carDetailsLabels, ordersLabels } from "@/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trash2, ExternalLink, Clock, Calendar, Trophy } from "lucide-react";
import { LicensePlate } from "../my-auctions/LicensePlate";
import { ListingBiddingParticipation } from "@/types/listing";
import { calculateTimeRemaining, formatDate } from "@/utils/dateUtils";
import { useListing } from "@/hooks/useListing";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ReservePriceStatus,
  getReservePriceReached,
} from "@/components/car-listing/bidding/ReservePriceStatus";

interface BiddingCardsProps {
  listings: ListingBiddingParticipation[];
  onRemove?: (listingId: string) => void;
}

// Helper function to determine card status and styling
const getCardStatus = (
  listing: ListingBiddingParticipation,
  b: Record<string, string>,
): {
  status:
    | "leading-active"
    | "not-leading-active"
    | "won"
    | "lost"
    | "reserved"
    | "cancelled";
  cardColor: string;
  statusText: string;
  statusBadgeColor: string;
} => {
  const isActive = listing.status === "active";
  const isCancelled = listing.status === "cancelled";
  const isReserved = listing.status === "reserved";
  const isWinner = listing.isWinner ?? false;
  const reservedForMe = listing.reservedForMe ?? false;
  const isLeading = listing.bidding?.isCurrentUserHighestBidder ?? false;

  if (isCancelled) {
    return {
      status: "cancelled",
      cardColor:
        "bg-gray-100 dark:bg-gray-900/30 dark:backdrop-blur border-2 border-gray-400 dark:border-gray-600",
      statusText: b.auctionCancelled,
      statusBadgeColor:
        "bg-gray-200 text-gray-900 border-gray-300 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-700",
    };
  }

  if (isReserved) {
    return {
      status: "reserved",
      cardColor:
        "bg-blue-100 dark:bg-blue-900/30 dark:backdrop-blur border-2 border-blue-400 dark:border-blue-600",
      statusText: reservedForMe ? b.reservedByYou : b.reservedByOther,
      statusBadgeColor:
        "bg-blue-200 text-blue-900 border-blue-300 dark:bg-blue-800 dark:text-blue-100 dark:border-blue-700",
    };
  }

  if (isActive && isLeading) {
    return {
      status: "leading-active",
      cardColor:
        "bg-green-100 dark:bg-green-900/30 dark:backdrop-blur border-2 border-green-400 dark:border-green-600",
      statusText: b.youLead,
      statusBadgeColor:
        "bg-green-200 text-green-900 border-green-300 dark:bg-green-800 dark:text-green-100 dark:border-green-700",
    };
  }

  if (isActive && !isLeading) {
    return {
      status: "not-leading-active",
      cardColor:
        "bg-orange-100 dark:bg-orange-900/30 dark:backdrop-blur border-2 border-orange-400 dark:border-orange-600",
      statusText: b.youAreOutbid,
      statusBadgeColor:
        "bg-orange-200 text-orange-900 border-orange-300 dark:bg-orange-800 dark:text-orange-100 dark:border-orange-700",
    };
  }

  if (!isActive && isWinner) {
    return {
      status: "won",
      cardColor:
        "bg-gradient-to-br from-amber-200 to-green-300 dark:from-green-800/50 dark:to-amber-800/50 dark:backdrop-blur border-8 border-amber-400 dark:border-amber-500 shadow-2xl shadow-amber-300/60 dark:shadow-amber-800/60 ring-4 ring-amber-300/70 dark:ring-amber-600/70 border-opacity-90",
      statusText: b.youWon,
      statusBadgeColor:
        "bg-gradient-to-r from-amber-400 to-yellow-400 text-amber-900 border-2 border-amber-500 dark:from-amber-600 dark:to-yellow-600 dark:text-amber-100 dark:border-amber-500 font-bold shadow-md animate-pulse",
    };
  }

  // Lost (ended and someone else won)
  return {
    status: "lost",
    cardColor:
      "bg-red-100 dark:bg-red-900/30 border-2 dark:backdrop-blur border-red-400 dark:border-red-600",
    statusText: b.youLost,
    statusBadgeColor:
      "bg-red-200 text-red-900 border-red-300 dark:bg-red-800 dark:text-red-100 dark:border-red-700",
  };
};

// Check if listing can be removed (all except active where user is leading)
const canRemoveListing = (listing: ListingBiddingParticipation): boolean => {
  const isActive = listing.status === "active";
  const isLeading = listing.bidding?.isCurrentUserHighestBidder ?? false;
  return !(isActive && isLeading);
};

// Individual Bidding Card Component
interface BiddingCardProps {
  listing: ListingBiddingParticipation;
  canRemove: boolean;
  onDeleteClick: (listingId: string, e: React.MouseEvent) => void;
  onNavigateClick: (
    listingId: string,
    campaignId: string | undefined,
    e: React.MouseEvent,
  ) => void;
  b: Record<string, string>;
  o: ReturnType<typeof ordersLabels>;
}

const BiddingCard = ({
  listing,
  canRemove,
  onDeleteClick,
  onNavigateClick,
  b,
  o,
}: BiddingCardProps) => {
  const { language } = useLanguage();
  const biddingLabels = carDetailsLabels(language).bidding;
  const cardStatus = getCardStatus(listing, b);

  // Calculate time remaining for active auctions
  const [timeRemaining, setTimeRemaining] = useState<{
    timeString: string;
    isUrgent: boolean;
  } | null>(null);

  useEffect(() => {
    if (listing.status === "active" && listing.endTime) {
      const updateTime = () => {
        const result = calculateTimeRemaining(listing.endTime);
        setTimeRemaining(result);
      };

      updateTime();
      const interval = setInterval(updateTime, 60000); // Update every minute

      return () => clearInterval(interval);
    } else {
      setTimeRemaining(null);
    }
  }, [listing.status, listing.endTime]);

  // Get price/bid information
  const currentBid =
    listing.auctionDetails?.currentBid ||
    listing.auctionDetails?.startingPrice ||
    0;
  const reservePrice =
    (
      listing.auctionDetails as {
        reservationPrice?: number;
        reservePrice?: number;
      }
    )?.reservationPrice ??
    (
      listing.auctionDetails as {
        reservationPrice?: number;
        reservePrice?: number;
      }
    )?.reservePrice ??
    null;
  const hasReservePrice = reservePrice != null && reservePrice > 0;
  const reservePriceReached = getReservePriceReached(reservePrice, currentBid);
  const isBuyNowTriggered = listing.auctionDetails?.isBuyNowTriggered === true;
  const soldPrice =
    listing.status === "completed"
      ? ((isBuyNowTriggered
          ? (listing.auctionDetails?.currentBuyNowPrice ??
            listing.auctionDetails?.buyNowFixed)
          : listing.auctionDetails?.currentBid) ?? null)
      : null;

  // Determine color for "Nuvarande bud" text based on status
  const getCurrentBidColor = (): string => {
    const isActive = listing.status === "active";
    const isCancelled = listing.status === "cancelled";
    const isReserved = listing.status === "reserved";
    const isWinner = listing.isWinner ?? false;
    const reservedForMe = listing.reservedForMe ?? false;
    const isLeading = listing.bidding?.isCurrentUserHighestBidder ?? false;

    if (isCancelled) {
      return "text-gray-600 dark:text-gray-400";
    }

    if (isReserved) {
      return reservedForMe
        ? "text-green-700 dark:text-green-300"
        : "text-red-700 dark:text-red-300";
    }

    if (isActive) {
      // Active auction
      if (isLeading) {
        return "text-green-700 dark:text-green-300"; // I'm leading
      } else {
        return "text-orange-700 dark:text-orange-300"; // Someone outbid me
      }
    } else {
      // Completed auction
      if (isWinner) {
        return "text-green-700 dark:text-green-300"; // I won
      } else {
        return "text-red-700 dark:text-red-300"; // I lost
      }
    }
  };

  const currentBidColor = getCurrentBidColor();

  return (
    <div className="w-full">
      <Card
        className={`w-full max-w-full overflow-hidden ${cardStatus.cardColor} shadow-lg transition-all hover:shadow-xl`}
      >
        <CardContent className="p-0">
          {/* Main Content: Image left, Info right */}
          <div className="flex flex-col md:flex-row">
            {/* Image - Left, stretches to edges */}
            {listing.thumbnail && (
              <div className="relative flex-shrink-0 h-40 md:h-auto w-full md:w-2/5 overflow-hidden">
                <NativeImage
                  src={listing.thumbnail}
                  alt={`${listing.vehicleDetails?.make} ${listing.vehicleDetails?.model}`}
                  className="w-full h-full object-cover"
                  style={{
                    minHeight: "100%",
                    maxHeight: "180px",
                    objectPosition: "center",
                  }}
                />
              </div>
            )}

            {/* Info - Right */}
            <div className="flex-1 p-3 md:p-4 flex flex-col">
              {/* Title with buttons */}
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm md:text-base font-semibold text-gray-900 dark:text-gray-100">
                    {listing.title}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">
                    {listing.vehicleDetails?.make || o.card.unknown}{" "}
                    {listing.vehicleDetails?.model || o.card.unknown}{" "}
                    {listing.vehicleDetails?.year || 0}
                  </p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {canRemove && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                      onClick={(e) => onDeleteClick(listing.id, e)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs gap-1 bg-white/50 dark:bg-black/20"
                    onClick={(e) =>
                      onNavigateClick(listing.id, listing.campaignId, e)
                    }
                  >
                    <ExternalLink className="w-3 h-3" />
                    <span className="hidden md:inline">
                      {o.card.goToListing}
                    </span>
                    <span className="md:hidden">{b.goShort}</span>
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-1.5 flex-wrap mb-2">
                {listing.vehicleDetails?.regNumber && (
                  <LicensePlate lpn={listing.vehicleDetails.regNumber} />
                )}
                <Badge
                  variant="outline"
                  className={`${cardStatus.statusBadgeColor} text-xs px-2 py-0.5 flex items-center gap-1`}
                >
                  {cardStatus.status === "won" && (
                    <Trophy className="w-3.5 h-3.5 text-amber-700 dark:text-amber-300" />
                  )}
                  {cardStatus.statusText}
                </Badge>
              </div>

              {/* Bid Info */}
              <div className="space-y-2">
                {/* Time remaining (for active auctions) */}
                {listing.status === "active" && timeRemaining && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4 text-accent" />
                      <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                        {b.timeLeft}:
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300">
                        {timeRemaining.timeString}
                      </span>
                      {timeRemaining.isUrgent && (
                        <Badge
                          variant="outline"
                          className="bg-red-100 text-red-800 border-red-200 text-xs py-0"
                        >
                          {b.urgent}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Current bid */}
                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                    {b.currentBid}
                  </span>
                  <span
                    className={`text-sm md:text-base font-bold ${currentBidColor}`}
                  >
                    {currentBid.toLocaleString("sv-SE")} kr
                  </span>
                </div>

                {/* My highest bid */}
                {listing.bidding?.currentUserHighestBid && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                      {b.myHighestBid}
                    </span>
                    <span className="text-sm md:text-base font-semibold text-gray-800 dark:text-gray-200">
                      {listing.bidding.currentUserHighestBid.toLocaleString(
                        "sv-SE",
                      )}{" "}
                      kr
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                    {o.card.updated}
                  </span>
                  <span className="text-xs md:text-sm text-gray-600 dark:text-gray-300 tabular-nums">
                    {listing.updatedAt
                      ? new Date(listing.updatedAt).toLocaleString("sv-SE", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : o.card.unknown}
                  </span>
                </div>

                {/* Reserve price status (same as BiddingInfo) */}
                {listing.status === "active" &&
                  hasReservePrice &&
                  reservePriceReached !== null && (
                    <ReservePriceStatus
                      reached={reservePriceReached}
                      label={
                        reservePriceReached
                          ? biddingLabels.reservePriceReached
                          : biddingLabels.reservePriceNotReached
                      }
                      infoTitle={
                        biddingLabels.reservePriceInfoTitle ??
                        "Vad är reservationspris?"
                      }
                      infoDescription={
                        biddingLabels.reservePriceInfoDescription ??
                        "Reservationspriset är det lägsta belopp säljaren är villig att acceptera."
                      }
                    />
                  )}

                {/* Sold info (for ended auctions) */}
                {listing.status === "completed" && (
                  <>
                    {soldPrice && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                          {b.soldPrice}
                        </span>
                        <span
                          className={`text-sm md:text-base font-bold ${
                            listing.isWinner
                              ? "text-green-700 dark:text-green-300"
                              : "text-red-700 dark:text-red-300"
                          }`}
                        >
                          {soldPrice.toLocaleString("sv-SE")} kr
                        </span>
                      </div>
                    )}
                    {listing.endTime && (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                          <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                            {b.ended}
                          </span>
                        </div>
                        <span className="text-xs md:text-sm text-gray-700 dark:text-gray-300">
                          {formatDate(listing.endTime, {
                            includeTime: false,
                          })}
                        </span>
                      </div>
                    )}
                  </>
                )}

                {/* Reserved info (for reserved auctions) */}
                {listing.status === "reserved" &&
                  isBuyNowTriggered &&
                  (() => {
                    const reservedPrice =
                      listing.auctionDetails?.currentBuyNowPrice ??
                      listing.auctionDetails?.buyNowFixed ??
                      null;

                    if (reservedPrice == null) return null;

                    return (
                      <div className="flex items-center justify-between">
                        <span className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                          {b.reservedPrice}
                        </span>
                        <span className="text-sm md:text-base font-bold text-blue-700 dark:text-blue-300">
                          {Number(reservedPrice).toLocaleString("sv-SE")} kr
                        </span>
                      </div>
                    );
                  })()}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const BiddingCards = ({ listings, onRemove }: BiddingCardsProps) => {
  const { language } = useLanguage();
  const o = ordersLabels(language);
  const b = o.biddings as Record<string, string>;
  const navigate = useNavigate();
  const { removeAuctionFromMyBiddingsAsync, isRemovingAuctionFromMyBiddings } =
    useListing();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [listingToDelete, setListingToDelete] = useState<string | null>(null);

  const handleNavigateToListing = useCallback(
    (
      listingId: string,
      campaignId: string | undefined,
      e: React.MouseEvent,
    ) => {
      e.stopPropagation();
      if (campaignId) {
        navigate(`/auctions/${campaignId}/${listingId}`, {
          state: { from: "/orders" },
        });
      }
    },
    [navigate],
  );

  const handleDeleteClick = useCallback(
    (listingId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setListingToDelete(listingId);
      setDeleteDialogOpen(true);
    },
    [],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!listingToDelete) return;

    try {
      await removeAuctionFromMyBiddingsAsync({
        listingId: listingToDelete,
      });

      if (onRemove) {
        onRemove(listingToDelete);
      }
      setDeleteDialogOpen(false);
      setListingToDelete(null);
    } catch (error) {
      // Optionally show error toast/notification here
    }
  }, [listingToDelete, removeAuctionFromMyBiddingsAsync, onRemove]);

  if (listings.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">{o.empty.noBiddingsToShow}</div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3 md:space-y-4 w-full">
        {listings.map((listing) => {
          const canRemove = canRemoveListing(listing);
          return (
            <BiddingCard
              key={listing.id}
              listing={listing}
              canRemove={canRemove}
              onDeleteClick={handleDeleteClick}
              onNavigateClick={handleNavigateToListing}
              b={b}
              o={o}
            />
          );
        })}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{b.removeDialogTitle}</DialogTitle>
            <DialogDescription>{b.removeDialogDescription}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setListingToDelete(null);
              }}
              disabled={isRemovingAuctionFromMyBiddings}
            >
              {o.contractPopup?.cancel ?? "Avbryt"}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isRemovingAuctionFromMyBiddings}
            >
              {isRemovingAuctionFromMyBiddings ? b.removing : b.remove}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
